import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { useArrival } from './arrival';

/** Arriving: the same 220ms as everything else that does. */
const OPEN_MS = 220;
/** Leaving, and it is always the quicker of the two. */
const CLOSE_MS = 180;

/**
 * Two curves, and the second is not the first run backwards.
 *
 * `--ease-out` in the stylesheet, and it is fast off the mark and settles at the
 * end: right for something arriving. Run on the way out it spends sixty per cent
 * of the distance in the first frame and then creeps the rest, so the panel
 * visibly goes and then hangs there for another tenth of a second — which is
 * what the owner filmed on 2026-08-21 and called a block appearing for a moment
 * on close. Measured off that recording: 132px, then 51, 17, 6, 1, 0.
 *
 * The addendum's prose says «180 мс той же кривой» and its own live frames run
 * the way out on the other one. The frames were right, and they were the half
 * that could be tapped.
 */
const ARRIVING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
// C-016 (FND-1611): leaving takes the arriving curve too, fast off the mark and settling,
// as Vitali chose on the motion playground. Was 'cubic-bezier(0.4, 0, 1, 1)', the old app's.
const LEAVING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

/**
 * A panel that opens in place, from nothing to the height of its own contents,
 * and closes the same way.
 *
 * Everything that appears under a tap goes through here — the roster's menu, the
 * row that unfolds into a card, the schedule's list of answers, the two on the
 * regent's panel, the choir switcher. Before it they arrived at full height in
 * one frame and shoved the page down under the finger, which is the jerk the
 * owner named on 2026-08-19.
 *
 * Closing was not animated until 2026-08-21 — "these unmount, and somebody who
 * has decided to collapse a panel is not waiting to watch it happen", said the
 * comment that used to stand here. That turned out to be the source of the
 * remaining sharpness: the arrival is seen and the departure happens between
 * frames, and an asymmetric pair reads as a jerk however smooth its better
 * half. Hence `open` rather than a caller that mounts and unmounts: the panel
 * has to outlive the decision to close it by the length of its own animation.
 *
 * The height is measured rather than guessed. The obvious pure-CSS routes both
 * fail here. A `max-height` keyframe needs a ceiling nobody can know: the one
 * this replaced ran to 400px, and the profile form went past that the day it
 * grew a birthday field, so it was clipped mid-animation and then snapped open.
 * The grid `0fr -> 1fr` trick needs `overflow: hidden` on the grid item, and
 * these panels are flex columns — with the overflow on them the row collapses
 * to their padding and border, 26px for a menu of four buttons. Measuring is
 * the honest way, and one `scrollHeight` read in a layout effect is the whole
 * cost. It was confirmed again in the frames: the first live version of them
 * was written on `0fr/1fr` and did not open at all.
 *
 * The overflow is only on while it runs. Left on it would clip the panel's own
 * elevation shadow, which is drawn outside its box.
 *
 * Nothing is animated for somebody who asked for less movement — the app turns
 * motion off wholesale rather than shortening it, and a Web Animations call
 * does not see the stylesheet's `prefers-reduced-motion` block.
 */
export default function Unfold({ open, children }: { open: boolean; children: ReactNode }) {
  const frame = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  /** Whether the panel is in the DOM. It outlives `open` by one closing run. */
  const [mounted, setMounted] = useState(open);
  /**
   * What the last run animated towards. It starts closed whatever `open` says,
   * so a panel mounted already open still arrives — which is how the two places
   * that render this conditionally, because the panel replaces what stood above
   * it rather than joining it, go on behaving exactly as they did.
   *
   * Except inside a screen that is itself still arriving — C-023 (`FND-1618`).
   * «One arrival has one owner» (`arrival.tsx`): a panel that mounts open while
   * the screen around it is still riding in is part of that arrival, and a fold
   * of its own on top would be the second run the rule forbids — the banner
   * growing out of the schedule while the schedule is still fading in, which is
   * what the audit of 2026-10-27 measured. So it starts where `open` says and
   * runs only when `open` changes under it afterwards.
   */
  const arrival = useArrival();
  const at = useRef(open && arrival !== null && performance.now() < arrival.endsAt);
  /**
   * The contents as they were while the panel was open. What closes a panel is
   * often the disappearance of what was in it — a refusal that cleared, a row
   * that was answered — and without this the box would animate shut empty,
   * which is the same jerk arriving from the other end.
   */
  const held = useRef(children);
  if (open) held.current = children;

  useLayoutEffect(() => {
    // Opening takes two passes: this one puts the panel in the DOM, and the
    // next one has an element to measure.
    if (open && !mounted) {
      setMounted(true);
      return;
    }
    if (at.current === open) return;
    at.current = open;

    const element = frame.current;
    const inner = body.current;
    // Nothing is animated while the app is off screen either, and not only
    // because nobody is watching: a hidden document does not advance its
    // animation timeline, so the height would sit at the first keyframe until
    // the webview came back. Telegram minimises the app rather than closing it,
    // so that is a state this really reaches.
    const still =
      element === null ||
      inner === null ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      document.visibilityState !== 'visible';
    if (still) {
      if (!open) setMounted(false);
      return;
    }

    const duration = open ? OPEN_MS : CLOSE_MS;
    const easing = open ? ARRIVING : LEAVING;
    const full = element.scrollHeight;
    element.classList.add('unfold--running');
    if (open) {
      // A Web Animation does not apply its first frame where it is created: the
      // first sample lands on the next tick, and the frame the browser paints
      // in between shows the panel at its full natural height. That is the flash
      // the owner saw on 2026-08-21 - the actions appearing whole for an instant
      // and then starting to grow from nothing. Closing needs no such priming:
      // full height is where it starts anyway.
      //
      // An animation outranks an inline style, so this shows in that one frame
      // and nowhere else, and `done` takes it off again - without which the
      // panel would collapse back to nothing the moment the run ended.
      element.style.height = '0px';
      element.style.opacity = '0';
    }
    const shut = { height: '0px', opacity: 0 };
    const grown = { height: `${full}px`, opacity: 1 };
    const box = element.animate(open ? [shut, grown] : [grown, shut], { duration, easing });
    // The six pixels ride on the contents and not on the box: on the box they
    // would carry the clipped frame up with them, and what has to read as
    // having come out from under the button is what is inside it. Six and not
    // more — enough to be a direction, not enough to be a slide.
    const under = { transform: 'translateY(-6px)' };
    const home = { transform: 'translateY(0)' };
    inner.animate(open ? [under, home] : [home, under], { duration, easing });

    // A run cancelled by the opposite tap must not finish the job of the tap it
    // replaced: without this, reopening mid-close unmounts the panel that is on
    // its way back in.
    let live = true;
    const done = () => {
      // Closing ends by holding the last frame rather than by letting go of it.
      // A Web Animation with no fill stops applying the moment it finishes, and
      // `setMounted(false)` is a state change React paints on the next tick -
      // so between the two the panel snapped back to its full height and full
      // opacity for one frame and then vanished. That flash is «они скрываются
      // и потом снова появляются», reported 2026-08-24; measured on the rig at
      // 46px and opacity 1, eight milliseconds before the unmount.
      if (live && !open) {
        element.style.height = '0px';
        element.style.opacity = '0';
        setMounted(false);
        return;
      }
      element.classList.remove('unfold--running');
      element.style.height = '';
      element.style.opacity = '';
    };
    void box.finished.then(done).catch(done);
    return () => {
      live = false;
      box.cancel();
      // Both come off on the way out too: an animation cancelled by an unmount
      // that raced the effect would otherwise leave the panel clipped, and the
      // priming above would leave it at nothing.
      element.classList.remove('unfold--running');
      element.style.height = '';
      element.style.opacity = '';
    };
  }, [open, mounted]);

  if (!mounted) return null;

  return (
    <div className="unfold" ref={frame}>
      <div className="unfold-body" ref={body}>
        {held.current}
      </div>
    </div>
  );
}

/**
 * C-016 (FND-1611): a panel that folds through `Unfold` only where a tap folds it.
 *
 * The date wheel on «Когда» and the birthday wheel are each drawn two ways: behind a
 * control that opens and closes them, and standing open as part of the form where there is
 * no such control. Only the first is a fold. Put through `Unfold`, the second would arrive
 * by growing from nothing on mount (`at` starts closed, above), an entrance nobody's
 * finger asked for, inside a sheet that may itself still be arriving. So where `fold` is
 * false this draws its contents while `open`, exactly as the `{open && …}` it replaces.
 */
export function FoldWhere({
  fold,
  open,
  children,
}: {
  fold: boolean;
  open: boolean;
  children: ReactNode;
}) {
  if (fold) return <Unfold open={open}>{children}</Unfold>;
  return open ? <>{children}</> : null;
}
